import React, { useState } from 'react';
import { MessageSquare, Reply, ThumbsUp, Heart, ChevronDown } from 'lucide-react';
import { TODAY } from '@/data/financial-systems';
import { COMPREHENSIVE_SCENARIOS } from '@/data/scenario-data';
import type { ScenarioComment } from '@/types/scenarios';

const fmtDateTime = (d: Date) =>
  d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

interface FlatComment {
  id: string;
  scenarioId: string;
  scenarioName: string;
  authorName: string;
  userRole: string;
  text: string;
  createdAt: Date;
  isReply: boolean;
  parentAuthorName?: string;
  likes: number;
  loves: number;
}

function flattenComments(): FlatComment[] {
  const results: FlatComment[] = [];
  for (const scenario of COMPREHENSIVE_SCENARIOS) {
    for (const comment of (scenario.comments ?? [])) {
      results.push({
        id: comment.id,
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        authorName: comment.authorName,
        userRole: comment.userRole,
        text: comment.text,
        createdAt: comment.createdAt,
        isReply: false,
        likes: comment.reactions?.like ?? 0,
        loves: comment.reactions?.love ?? 0,
      });
      // Flatten replies
      for (const reply of (comment.replies ?? [])) {
        results.push({
          id: reply.id,
          scenarioId: scenario.id,
          scenarioName: scenario.name,
          authorName: reply.authorName,
          userRole: reply.userRole,
          text: reply.text,
          createdAt: reply.createdAt,
          isReply: true,
          parentAuthorName: comment.authorName,
          likes: reply.reactions?.like ?? 0,
          loves: reply.reactions?.love ?? 0,
        });
      }
    }
  }
  return results.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

// Get unique authors for filter
function getAuthors(entries: FlatComment[]): string[] {
  return Array.from(new Set(entries.map(e => e.authorName))).sort();
}

export const ScenarioDiscussionAuditPreview: React.FC = () => {
  const [scenarioFilter, setScenarioFilter] = useState<string>('all');
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const reportDate = TODAY.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const allEntries = flattenComments();

  const scenarios = Array.from(new Set(allEntries.map(e => e.scenarioId))).map(id => ({
    id,
    name: allEntries.find(e => e.scenarioId === id)?.scenarioName ?? id,
  }));
  const authors = getAuthors(allEntries);

  const filtered = allEntries.filter(e =>
    (scenarioFilter === 'all' || e.scenarioId === scenarioFilter) &&
    (authorFilter === 'all' || e.authorName === authorFilter),
  );

  const replyCount = allEntries.filter(e => e.isReply).length;
  const topLevelCount = allEntries.length - replyCount;
  const scenariosWithComments = scenarios.length;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Scenario Discussion Audit</h3>
          <p className="text-sm text-muted-foreground">BuildRight Construction Ltd · All scenario comments and discussion threads</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{allEntries.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Messages</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{topLevelCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Top-level Comments</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{replyCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Replies</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-2xl font-bold">{scenariosWithComments}</p>
          <p className="text-xs text-muted-foreground mt-1">Scenarios Discussed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">Scenario:</label>
          <select
            value={scenarioFilter}
            onChange={e => setScenarioFilter(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background"
          >
            <option value="all">All scenarios</option>
            {scenarios.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-medium">Author:</label>
          <select
            value={authorFilter}
            onChange={e => setAuthorFilter(e.target.value)}
            className="text-xs border rounded px-2 py-1 bg-background"
          >
            <option value="all">All authors</option>
            {authors.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        {(scenarioFilter !== 'all' || authorFilter !== 'all') && (
          <button onClick={() => { setScenarioFilter('all'); setAuthorFilter('all'); }} className="text-xs text-muted-foreground underline hover:no-underline">
            Clear filters
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} message{filtered.length !== 1 ? 's' : ''} shown</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date / Time</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Scenario</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Author</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Role</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
              <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Reactions</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.map(entry => {
              const isExpanded = expandedId === entry.id;
              const preview = entry.text.length > 80 ? `${entry.text.slice(0, 80)}…` : entry.text;
              return (
                <React.Fragment key={entry.id}>
                  <tr className={`hover:bg-muted/30 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{fmtDateTime(entry.createdAt)}</td>
                    <td className="px-4 py-2.5 font-medium max-w-[160px] truncate">{entry.scenarioName}</td>
                    <td className="px-4 py-2.5">{entry.authorName}</td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{entry.userRole}</td>
                    <td className="px-4 py-2.5">
                      {entry.isReply ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 text-xs font-medium dark:bg-violet-950/30 dark:border-violet-800">
                          <Reply className="h-3 w-3" />
                          Reply
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 text-xs font-medium dark:bg-blue-950/30 dark:border-blue-800">
                          <MessageSquare className="h-3 w-3" />
                          Comment
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {(entry.likes > 0 || entry.loves > 0) ? (
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          {entry.likes > 0 && <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{entry.likes}</span>}
                          {entry.loves > 0 && <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{entry.loves}</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        {isExpanded ? 'Hide' : 'Show'}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-muted/20">
                      <td colSpan={7} className="px-4 py-3">
                        {entry.isReply && entry.parentAuthorName && (
                          <p className="text-xs text-muted-foreground mb-1">↩ In reply to {entry.parentAuthorName}</p>
                        )}
                        <p className="text-sm text-foreground">{entry.text}</p>
                      </td>
                    </tr>
                  )}
                  {!isExpanded && entry.text.length > 80 && (
                    <tr className="bg-transparent">
                      <td colSpan={7} className="px-4 pb-2 text-xs text-muted-foreground italic">{preview}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No comments found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-4">
        <MessageSquare className="h-3.5 w-3.5" />
        <span>{allEntries.length} total messages across {scenariosWithComments} scenarios</span>
      </div>
    </div>
  );
};
